import { APIClient, type ApiError } from "../../api/client.js";
import { accordion } from "../../components/Accordion.js";
import { confirmDialog, promptDialog } from "../../components/Modal.js";
import { toast } from "../../components/Toast.js";
import { h } from "../../components/dom.js";
import { formatGb } from "../../components/format.js";
import { linkHuggingFace, unlinkHuggingFace } from "../../services/hugging-face.js";
import type { WorkspaceDef } from "../../shell/types.js";
import { store } from "../../state/store.js";
import type {
	ArtifactStorageStatus,
	AuthUser,
	GenerationJob,
	UsageSummary,
	WorkerCapabilities,
} from "../../types/index.js";
/**
 * Workspace Compte — auth (connexion/inscription/vérification/réinit),
 * machines connectées, codes d'appairage, jobs hébergés, téléchargement du
 * launcher. Reproduit le comportement de l'ancien JobsUI : pas de solde de
 * crédits affiché, refresh toutes les 5 s, token de session réutilisé.
 */
import { createLinkRequestCard } from "./LinkRequestCard.js";

const JOB_KIND_LABELS: Record<string, string> = { image: "Image", video: "Vidéo", audio: "Voix" };
const JOB_STATUS_LABELS: Record<string, string> = {
	queued: "en attente",
	running: "en cours",
	succeeded: "terminée",
	failed: "échouée",
	cancelled: "annulée",
};

export const accountWorkspace: WorkspaceDef = {
	id: "account",
	title: "Compte",
	icon: "account",
	build(container) {
		let mode: "login" | "register" = "login";
		let lastEmail = "";
		let refreshTimer: number | undefined;

		// --- Téléchargement du launcher ---
		const downloadsView = h("div", { class: "card" });
		renderDownloads(downloadsView);
		// Une fois un ordinateur relié, les téléchargements ne sont plus l'étape
		// à faire : repliés derrière ce bouton plutôt qu'en tête de page.
		let downloadsOpenedByHand = false;
		const otherComputerBtn = h(
			"button",
			{
				class: "btn btn-small",
				type: "button",
				hidden: true,
				onclick: () => {
					downloadsOpenedByHand = true;
					downloadsView.hidden = false;
					otherComputerBtn.hidden = true;
				},
			},
			"Installer le programme sur un autre ordinateur"
		) as HTMLButtonElement;

		// --- Vue non connectée ---
		const emailInput = h("input", {
			type: "email",
			autocomplete: "email",
			placeholder: "Email",
			required: true,
		}) as HTMLInputElement;
		const passwordInput = h("input", {
			type: "password",
			autocomplete: "current-password",
			placeholder: "Mot de passe (12 caractères min.)",
			minLength: 12,
			required: true,
		}) as HTMLInputElement;
		const authStatus = h("p", { class: "help-text", role: "status", "aria-live": "polite" });
		const authSubmit = h(
			"button",
			{ class: "btn btn-primary", type: "submit" },
			"Se connecter"
		) as HTMLButtonElement;
		const loginModeBtn = h(
			"button",
			{ class: "btn btn-small", type: "button" },
			"Connexion"
		) as HTMLButtonElement;
		const registerModeBtn = h(
			"button",
			{ class: "btn btn-small", type: "button" },
			"Inscription"
		) as HTMLButtonElement;
		const authForm = h(
			"form",
			{
				class: "auth-form",
				onsubmit: (event: Event) => {
					event.preventDefault();
					void submitAuth();
				},
			},
			h("div", { class: "file-input-row" }, loginModeBtn, registerModeBtn),
			emailInput,
			passwordInput,
			authSubmit,
			authStatus,
			h(
				"div",
				{ class: "file-input-row" },
				h(
					"button",
					{ class: "btn btn-small", type: "button", onclick: () => void resendVerification() },
					"Renvoyer l'email de confirmation"
				),
				h(
					"button",
					{ class: "btn btn-small", type: "button", onclick: () => void requestRecovery() },
					"Mot de passe oublié ?"
				)
			)
		) as HTMLFormElement;

		// --- Vue connectée ---
		const accountEmail = h("span", { class: "muted" });
		const verifyBanner = h(
			"div",
			{ class: "result-warning" },
			h("span", null, "Confirmez votre adresse email pour pouvoir générer. "),
			h(
				"button",
				{ class: "btn btn-small", type: "button", onclick: () => void resendVerification() },
				"Renvoyer l'email"
			)
		);
		const workersView = h("div");
		const pairingResult = h("p", { class: "help-text", role: "status", "aria-live": "polite" });
		const sharedStatus = h("p", {
			class: "help-text",
			role: "status",
			"aria-live": "polite",
			hidden: true,
		});
		const usageView = h("p", { class: "help-text" });
		const jobsView = h("div");

		const accountView = h(
			"div",
			{ class: "workspace-panel" },
			h(
				"div",
				{ class: "card" },
				h(
					"div",
					{ class: "card-title" },
					h("span", null, "Compte : ", accountEmail),
					// No « Actualiser »: the page refreshes itself every five
					// seconds while it is shown (startRefresh).
					h(
						"button",
						{ class: "btn btn-small", type: "button", onclick: () => void logout() },
						"Déconnexion"
					)
				),
				verifyBanner
			),
			h(
				"div",
				{ class: "card" },
				h("div", { class: "card-title" }, "Vos ordinateurs"),
				workersView,
				sharedStatus,
				h(
					"p",
					{ class: "help-text" },
					"Vos images restent privées. Pour aider d'autres personnes avec votre ordinateur, " +
						"cochez « Partager ma puissance de calcul » dans le programme."
				),
				// Pour un ordinateur sans écran (serveur Linux) : le programme en
				// ligne de commande se relie avec ce code. Personne d'autre n'en a
				// besoin, d'où le repli.
				accordion({
					title: "Ordinateur sans écran",
					children: [
						h(
							"p",
							{ class: "help-text" },
							"Un serveur sans écran se relie en ligne de commande, avec un code à usage unique."
						),
						h(
							"button",
							{ class: "btn btn-small", type: "button", onclick: () => void createPairingCode() },
							"Créer un code"
						),
						pairingResult,
					],
				}).element
			),
			usageView,
			h("div", { class: "card" }, h("div", { class: "card-title" }, "Générations"), jobsView)
		);

		const authView = h(
			"div",
			{ class: "card" },
			h("div", { class: "card-title" }, "Votre compte"),
			authForm
		);

		// A computer asking to be linked (the program opened `/?relier=`):
		// above everything, signed in or not.
		const linkRequest = createLinkRequestCard();
		container.append(
			h("h2", { class: "workspace-title" }, "Votre compte et votre ordinateur"),
			linkRequest,
			h(
				"p",
				{ class: "help-text" },
				"Les images sont faites par votre propre ordinateur, gratuitement. Installez-y le " +
					"programme (~40 Mo), ouvrez-le et cliquez « Relier avec mon navigateur » : il s'occupe du reste."
			),
			downloadsView,
			otherComputerBtn,
			authView,
			accountView
		);

		// --- Comportements ---

		function setStatus(message: string, isError = false): void {
			authStatus.textContent = message;
			authStatus.style.color = isError ? "var(--error-color)" : "";
		}

		function setMode(next: "login" | "register"): void {
			mode = next;
			authSubmit.textContent = mode === "login" ? "Se connecter" : "Créer le compte";
			loginModeBtn.classList.toggle("btn-primary", mode === "login");
			registerModeBtn.classList.toggle("btn-primary", mode === "register");
			setStatus("");
		}
		loginModeBtn.addEventListener("click", () => setMode("login"));
		registerModeBtn.addEventListener("click", () => setMode("register"));
		setMode("login");

		/** Atterrissage depuis un lien email (/verify-email, /reset-password). */
		async function consumeUrlToken(): Promise<void> {
			const params = new URLSearchParams(window.location.search);
			const token = params.get("token");
			if (!token) return;
			const path = window.location.pathname;
			try {
				if (path.startsWith("/verify-email")) {
					const user = await APIClient.verifyEmail(token);
					setStatus(`Email ${user.email} vérifié. Connectez-vous.`);
				} else if (path.startsWith("/reset-password")) {
					const password = await promptDialog("Nouveau mot de passe (12 caractères min.)");
					if (!password || password.length < 12) {
						setStatus("Mot de passe trop court, réinitialisation annulée.", true);
					} else {
						await APIClient.resetPassword(token, password);
						setStatus("Mot de passe réinitialisé. Connectez-vous.");
					}
				}
			} catch (error) {
				setStatus(error instanceof Error ? error.message : "Lien invalide ou expiré.", true);
			} finally {
				window.history.replaceState(
					{},
					"",
					window.location.pathname.replace(/\/(verify-email|reset-password)$/, "/")
				);
			}
		}

		async function submitAuth(): Promise<void> {
			const email = emailInput.value.trim();
			const password = passwordInput.value;
			if (!email || !password) {
				setStatus("Email et mot de passe requis", true);
				return;
			}
			lastEmail = email;
			if (mode === "register" && password.length < 12) {
				setStatus("Le mot de passe doit contenir au moins 12 caractères", true);
				return;
			}
			setStatus("Connexion…");
			authSubmit.disabled = true;
			try {
				const session =
					mode === "login"
						? await APIClient.login(email, password)
						: await APIClient.register(email, password);
				showLoggedIn(session.user);
				await refresh();
			} catch (error) {
				setStatus(error instanceof Error ? error.message : "Authentification impossible", true);
			} finally {
				authSubmit.disabled = false;
			}
		}

		/**
		 * Réclamer la confirmation seulement là où le serveur l'exige.
		 *
		 * `email_verification_required` à false veut dire que cette personne
		 * peut générer tout de suite ; la page le réclamait quand même, faute
		 * de pouvoir le savoir. Inconnu (avant la première réponse de
		 * `/api/status`, ou face à un serveur plus ancien) = on le réclame,
		 * comme avant : mieux vaut une phrase de trop qu'un blocage muet.
		 */
		function renderVerifyBanner(): void {
			const user = store.get().user;
			const required = store.get().status?.email_verification_required !== false;
			verifyBanner.classList.toggle("hidden", !user || user.verified || !required);
		}
		// Le statut peut arriver après la connexion : la bannière se corrige.
		store.subscribe((s) => s.status?.email_verification_required ?? null, renderVerifyBanner);

		function showLoggedIn(user: AuthUser): void {
			authView.classList.add("hidden");
			accountView.classList.remove("hidden");
			accountEmail.textContent = user.email;
			lastEmail = user.email;
			store.setState({ user });
			renderVerifyBanner();
			startRefresh();
		}

		function showLoggedOut(): void {
			authView.classList.remove("hidden");
			accountView.classList.add("hidden");
			store.setState({ user: null, workers: [], executionTarget: { kind: "local" } });
			stopRefresh();
		}

		async function resendVerification(): Promise<void> {
			const email = lastEmail || emailInput.value.trim();
			if (!email) {
				setStatus("Renseignez d'abord votre email.", true);
				return;
			}
			// L'API répond toujours 202 : le message ne confirme jamais l'existence du compte.
			await APIClient.resendVerification(email).catch(() => undefined);
			setStatus("Si un compte non vérifié existe, un email vient d'être envoyé.");
		}

		async function requestRecovery(): Promise<void> {
			const email = lastEmail || emailInput.value.trim();
			if (!email) {
				setStatus("Renseignez d'abord votre email.", true);
				return;
			}
			await APIClient.requestPasswordRecovery(email).catch(() => undefined);
			setStatus("Si un compte existe, un lien de réinitialisation vient d'être envoyé.");
		}

		async function refresh(): Promise<void> {
			if (!APIClient.hasAuthToken()) return;
			try {
				// Pas d'appels crédits/gains : générer sur sa propre machine n'est jamais facturé.
				const [jobs, usage, workers] = await Promise.all([
					APIClient.listJobs(20),
					APIClient.getUsageSummary(),
					APIClient.listWorkers(),
				]);
				store.setState({ workers });
				renderJobs(jobs);
				renderUsage(usage);
				renderWorkers(workers);
				APIClient.getSharedComputeStatus().then(renderSharedStatus).catch(renderSharedStatusError);
			} catch (error) {
				if (error instanceof Error && (error as ApiError).status === 401) {
					APIClient.clearAuthToken();
					showLoggedOut();
					setStatus("Session expirée", true);
				}
			}
		}

		function renderJobs(jobs: GenerationJob[]): void {
			jobsView.replaceChildren();
			if (jobs.length === 0) {
				jobsView.appendChild(h("p", { class: "help-text" }, "Aucune génération pour l'instant."));
				return;
			}
			for (const job of jobs) {
				const cancelBtn =
					job.status === "queued" || job.status === "running"
						? h(
								"button",
								{
									class: "btn btn-small",
									type: "button",
									onclick: (e: Event) => void cancelJob(job.job_id, e.target as HTMLButtonElement),
								},
								"Annuler"
							)
						: null;
				jobsView.appendChild(
					h(
						"div",
						{ class: "job-item" },
						h(
							"div",
							null,
							h(
								"strong",
								null,
								`${JOB_KIND_LABELS[job.kind] ?? job.kind} — ${JOB_STATUS_LABELS[job.status] ?? job.status}`
							),
							h(
								"div",
								{ class: "job-meta" },
								[
									formatDate(job.created_at),
									job.route ? "sur votre ordinateur" : "en attente d'un ordinateur",
									job.attempt > 1 ? `essai ${job.attempt}/${job.max_attempts}` : null,
								]
									.filter(Boolean)
									.join(" · ")
							)
						),
						cancelBtn
					)
				);
			}
		}

		function renderUsage(usage: UsageSummary): void {
			usageView.textContent =
				usage.total_jobs === 0
					? ""
					: `${usage.total_jobs} génération${usage.total_jobs > 1 ? "s" : ""} · ${usage.completed_jobs} terminée${usage.completed_jobs > 1 ? "s" : ""}`;
		}

		function renderWorkers(workers: WorkerCapabilities[]): void {
			workersView.replaceChildren();
			const anyOnline = workers.some((worker) => worker.online);
			if (!downloadsOpenedByHand) {
				downloadsView.hidden = anyOnline;
				otherComputerBtn.hidden = !anyOnline;
			}
			if (workers.length === 0) {
				workersView.appendChild(
					h(
						"p",
						{ class: "help-text" },
						"Aucun ordinateur relié pour l'instant : installez le programme ci-dessus, " +
							"ouvrez-le et cliquez « Relier avec mon navigateur »."
					)
				);
				return;
			}
			for (const worker of workers) {
				// One fact per line, its name on the left: an auto-fit grid of
				// sentences wrapped them at different heights (30/10 audit).
				const specs: [string, string][] = [
					["Système", PLATFORM_NAMES[worker.platform] ?? worker.platform],
				];
				if (worker.vram_gb > 0) specs.push(["Carte graphique", formatGb(worker.vram_gb)]);
				specs.push(["Mémoire", formatGb(worker.ram_gb, 0)]);
				if (typeof worker.disk_free_gb === "number") {
					specs.push(["Disque libre", formatGb(worker.disk_free_gb, 0)]);
				}
				if (worker.agent_build) specs.push(["Programme", `version ${worker.agent_build}`]);
				workersView.appendChild(
					h(
						"div",
						{ class: "worker-card" },
						h(
							"div",
							{ class: "worker-head" },
							h("strong", null, computerName(worker)),
							h(
								"span",
								{ class: `badge ${worker.online ? "badge-success" : ""}` },
								worker.online ? "En ligne" : "Hors ligne"
							)
						),
						h(
							"dl",
							{ class: "worker-specs" },
							...specs.flatMap(([name, value]) => [h("dt", null, name), h("dd", null, value)])
						),
						huggingFaceRow(worker),
						// Only for a machine that is not reporting: removing the one
						// you are about to generate on would be a trap, and it would
						// come straight back on its next heartbeat anyway.
						worker.online
							? null
							: h(
									"button",
									{
										class: "btn btn-small",
										type: "button",
										onclick: (event: Event) =>
											void forgetWorker(worker, event.target as HTMLButtonElement),
									},
									"Retirer cet ordinateur"
								)
					)
				);
			}
		}

		/**
		 * The Hugging Face account this machine downloads gated models with.
		 * Only for a machine that is online and knows the site sign-in: an older
		 * program is told so by the Library's next step instead.
		 */
		function huggingFaceRow(worker: WorkerCapabilities): HTMLElement | null {
			if (!worker.online || !(worker.architectures ?? []).includes("hf_connect")) return null;
			const account = worker.hf_account;
			const button = h(
				"button",
				{
					class: "btn btn-small",
					type: "button",
					onclick: () => {
						button.setAttribute("disabled", "");
						const done = () => button.removeAttribute("disabled");
						if (account) {
							void unlinkHuggingFace(worker.worker_id).then((ok) => {
								if (ok) toast("Hugging Face délié de cet ordinateur.", "success");
								done();
							});
						} else {
							void linkHuggingFace(worker.worker_id).then(done);
						}
					},
				},
				account ? "Délier" : "Relier"
			);
			return h(
				"div",
				{ class: "worker-hf" },
				h("span", null, account ? `Hugging Face : ${account}` : "Hugging Face : non relié"),
				button
			);
		}

		async function forgetWorker(
			worker: WorkerCapabilities,
			button: HTMLButtonElement
		): Promise<void> {
			const name = computerName(worker);
			const confirmed = await confirmDialog(
				"Retirer cet ordinateur ?",
				`« ${name} » disparaîtra de la liste. Rien n'est désinstallé : si le programme s'y reconnecte, il reviendra de lui-même.`
			);
			if (!confirmed) return;
			button.disabled = true;
			try {
				await APIClient.forgetWorker(worker.worker_id);
				await refresh();
			} catch (error) {
				button.disabled = false;
				setStatus(
					error instanceof Error ? error.message : "L'ordinateur n'a pas pu être retiré.",
					true
				);
			}
		}

		// Le stockage des images est l'affaire du site : on n'en parle que s'il
		// ne marche pas, et sans son message technique (en anglais).
		function renderSharedStatus(status: ArtifactStorageStatus): void {
			sharedStatus.hidden = status.ready;
			sharedStatus.textContent = status.ready
				? ""
				: "Le site ne peut pas enregistrer de nouvelles images pour l'instant. Réessayez un peu plus tard.";
		}

		function renderSharedStatusError(): void {
			sharedStatus.hidden = true;
		}

		async function createPairingCode(): Promise<void> {
			pairingResult.textContent = "Génération du code…";
			try {
				const pairing = await APIClient.createPairingCode();
				pairingResult.textContent = `Code : ${pairing.code} — expire ${formatDate(pairing.expires_at)}`;
				await navigator.clipboard?.writeText(pairing.code);
			} catch (error) {
				pairingResult.textContent = error instanceof Error ? error.message : "Code indisponible";
			}
		}

		async function cancelJob(jobId: string, button: HTMLButtonElement): Promise<void> {
			button.disabled = true;
			try {
				await APIClient.cancelJob(jobId);
				await refresh();
			} catch (error) {
				toast(error instanceof Error ? error.message : "Annulation impossible", "error");
				// 409: it finished meanwhile -- show it finished rather than
				// leave a button that can no longer do anything.
				if ((error as ApiError).status === 409) await refresh();
				else button.disabled = false;
			}
		}

		async function logout(): Promise<void> {
			await APIClient.logout().catch(() => undefined);
			showLoggedOut();
		}

		function startRefresh(): void {
			stopRefresh();
			// Four or five requests every five seconds, forever: a tab left open
			// in the background asked them all day. Nobody reads a hidden tab, so
			// it asks nothing, and catches up the moment it is shown again.
			refreshTimer = window.setInterval(() => {
				if (!document.hidden) void refresh();
			}, 5000);
		}

		document.addEventListener("visibilitychange", () => {
			if (!document.hidden && refreshTimer !== undefined) void refresh();
		});

		function stopRefresh(): void {
			if (refreshTimer !== undefined) window.clearInterval(refreshTimer);
			refreshTimer = undefined;
		}

		void consumeUrlToken();
		// The program's « Créer un compte » and « Mot de passe oublié ? »
		// (agent/gui.py) land here: both used to open the home page, where
		// neither form was in sight.
		const asked = takeAccountRequest();
		void (async () => {
			if (!APIClient.hasAuthToken()) {
				showLoggedOut();
				// Next turn: this runs while the shell is still being built,
				// before the rail exists.
				if (asked) {
					window.setTimeout(() => {
						document
							.querySelector<HTMLButtonElement>('.rail-item[data-workspace="account"]')
							?.click();
						setMode(asked === "creer" ? "register" : "login");
						if (asked === "motdepasse") {
							setStatus("Saisissez votre email, puis « Mot de passe oublié ? ».");
						}
						emailInput.focus();
					}, 0);
				}
				return;
			}
			try {
				const user = await APIClient.getCurrentUser();
				showLoggedIn(user);
				await refresh();
			} catch {
				APIClient.clearAuthToken();
				showLoggedOut();
			}
		})();
	},
};

/** `?compte=creer` or `?compte=motdepasse`, taken out of the address. */
function takeAccountRequest(): "creer" | "motdepasse" | null {
	const params = new URLSearchParams(window.location.search);
	const asked = params.get("compte");
	if (asked !== "creer" && asked !== "motdepasse") return null;
	params.delete("compte");
	const rest = params.toString();
	window.history.replaceState({}, "", `${window.location.pathname}${rest ? `?${rest}` : ""}`);
	return asked;
}

function formatDate(iso: string): string {
	return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
}

function renderDownloads(view: HTMLElement): void {
	const base = "https://agent-runtimes.adrianguichard.dev";
	const platform = `${navigator.platform} ${navigator.userAgent}`.toLowerCase();
	const detected = platform.includes("win")
		? "windows"
		: platform.includes("linux") && !platform.includes("android")
			? "linux"
			: "";
	const builds = [
		{
			key: "windows",
			label: "Windows 64 bits",
			file: "ImageGeneratorAgentLauncherSetup.exe",
			note: "installeur, ~39 Mo",
		},
		{
			key: "linux",
			label: "Linux x86_64",
			file: "ImageGeneratorAgentLauncher-linux-x86_64.tar.gz",
			note: "archive, ~44 Mo",
		},
	];
	const onPhone = /android|iphone|ipad|mobile/.test(platform);
	view.replaceChildren(
		h("div", { class: "card-title" }, "Installer le programme sur votre ordinateur"),
		...(onPhone
			? [
					h(
						"p",
						{ class: "result-warning" },
						"Le programme s'installe sur un ordinateur (Windows ou Linux), pas sur ce téléphone. " +
							"Ouvrez cette page sur votre ordinateur pour le télécharger ; ensuite vous pourrez " +
							"créer des images d'ici aussi."
					),
				]
			: []),
		...builds.map((build) =>
			h(
				"div",
				{ class: "job-item" },
				h(
					"div",
					null,
					h(
						"a",
						{ href: `${base}/${build.file}` },
						build.label,
						build.key === detected && !onPhone ? " — votre système" : ""
					),
					h("div", { class: "job-meta" }, build.note)
				),
				h(
					"a",
					{
						class: "badge",
						href: `${base}/${build.file}.sha256`,
						title: "Empreinte SHA-256 du fichier, pour vérifier son téléchargement",
					},
					"empreinte"
				)
			)
		),
		// The installer is not code-signed (owner's decision, 2026-09-21), so
		// Windows stops the first launch behind a blue screen whose only
		// visible button is "Ne pas exécuter". Saying *that* it will warn
		// without saying *how* to get past it lost the newcomer at the one
		// step nobody can guess; name the two clicks instead.
		h(
			"p",
			{ class: "help-text" },
			"Sous Windows, le programme n'est pas encore signé numériquement. Au premier " +
				"lancement, Windows affiche « Windows a protégé votre ordinateur » : cliquez sur ",
			h("strong", null, "Informations complémentaires"),
			", puis sur ",
			h("strong", null, "Exécuter quand même"),
			". Votre navigateur peut aussi bloquer le fichier un instant : choisissez ",
			h("strong", null, "Conserver"),
			"."
		),
		h(
			"p",
			{ class: "help-text" },
			"Ensuite : ouvrez le programme et cliquez « Relier avec mon navigateur », puis confirmez ici. " +
				"Il prépare votre ordinateur tout seul, puis le site vous propose votre première image."
		)
	);
}

const PLATFORM_NAMES: Record<string, string> = {
	windows: "Windows",
	linux: "Linux",
	darwin: "macOS",
};

/** Ce que la personne reconnaît : sa carte graphique, ou « sans carte graphique ». */
function computerName(worker: WorkerCapabilities): string {
	if (worker.gpu_name) return worker.gpu_name;
	return worker.device_type === "cpu" ? "Ordinateur sans carte graphique" : "Votre ordinateur";
}
